import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Formatter for rate values expressed per kilowatt-hour ($/kWh).
 * Prepends a dollar sign to the formatted value for currency display.
 * Used for energy cost metrics and utility rate displays.
 *
 * @param {UnitValue} unitValue - The rate value and units to format
 * @param {any} unitValue.value - The numeric rate value
 * @param {string} [unitValue.units] - The current units of the value
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted numeric value with dollar sign prefix (e.g., '$0.12')
 *   - unitsLabel: The unit string (e.g., '/kWh')
 *   - valueUnitsLabel: Combined value and units without spacing (e.g., '$0.12/kWh')
 *
 * @example
 * formatRatePerKwh({ value: 0.12, units: '/kWh' })
 * // Returns: { valueLabel: '$0.12', unitsLabel: '/kWh', valueUnitsLabel: '$0.12/kWh' }
 *
 * @example
 * formatRatePerKwh({ value: 0.085, units: '' })
 * // Returns: { valueLabel: '$0.09', unitsLabel: '/kWh', valueUnitsLabel: '$0.09/kWh' }
 */
export const formatRatePerKwh = (
  unitValue: UnitValue,
  displayUnits?: string
): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);

  const formatted = unitValueToFormattedUnitLabels({
    value: formatNumber(transformed.value),
    units: transformed.units,
  });

  return {
    valueLabel: `${SUPPORTED_UNITS.$}${formatted.valueLabel}`,
    unitsLabel: `${formatted.unitsLabel}`,
    valueUnitsLabel: `${SUPPORTED_UNITS.$}${formatted.valueLabel}${formatted.unitsLabel}`,
  };
};
